import React from 'react';
import { Model, ModelCapability, ModelCategory } from './types';

/**
 * Helper function to parse hierarchical category
 * @param category The category string or object
 * @returns Parsed category object
 */
export const parseCategory = (category: string | ModelCategory): ModelCategory => {
    if (typeof category === 'object') {
        return category;
    }
    const [provider, family] = category.split('/');
    return { provider, family };
};

/**
 * Convert a category object or string to a string representation for UI
 * @param category The category object or string
 * @returns String representation of the category
 */
export const stringifyCategory = (category: string | ModelCategory): string => {
    if (typeof category === 'string') {
        return category;
    }
    return category.family
        ? `${category.provider}/${category.family}`
        : category.provider;
};

/**
 * Core models available in the application
 * Add or edit models directly in this file
 */
export const models: Model[] = [
    // OpenAI GPT-4.1 Family
    {
        id: 'gpt-4.1',
        name: 'GPT-4.1',
        description: 'GPT-4.1 is the successor to GPT-4o from OpenAI',
        category: {
            provider: 'OpenAI',
            family: 'GPT 4.1'
        },
        capabilities: [],
        icon: '/logos/openai.svg',
        planRequired: 'pro',
        provider: 'OpenAI',
        maxTokens: 2048
    },
    {
        id: 'gpt-4.1-mini',
        name: 'GPT-4.1-Mini',
        description: 'GPT-4.1-mini is the successor to GPT-4o-mini from OpenAI',
        category: {
            provider: 'OpenAI',
            family: 'GPT 4.1'
        },
        capabilities: [],
        icon: '/logos/openai.svg',
        planRequired: 'plus',
        provider: 'OpenAI',
        maxTokens: 2048
    },
    {
        id: 'gpt-4.1-nano',
        name: 'GPT-4.1-Nano',
        description: 'GPT-4.1-nano is the budget-friendly version of the GPT-4.1 family from OpenAI',
        category: {
            provider: 'OpenAI',
            family: 'GPT 4.1',
            showSeparator: true
        },
        capabilities: [],
        icon: '/logos/openai.svg',
        planRequired: 'free',
        provider: 'OpenAI',
        maxTokens: 2048
    },

    // OpenAI O4 Family (Standalone)
    {
        id: 'o4-mini',
        name: 'O4-Mini',
        description: 'Latest model from OpenAI',
        category: {
            provider: 'OpenAI',
            isStandalone: true,
            showSeparator: true
        },
        capabilities: ['reasoning'],
        icon: '/logos/openai.svg',
        planRequired: 'pro',
        provider: 'OpenAI',
        maxTokens: 2048
    },

    // Anthropic Models
    {
        id: 'claude-3-7-sonnet-20250219',
        name: 'Claude 3.7 Sonnet',
        description: 'Latest model from Anthropic (Reasoning available)',
        category: {
            provider: 'Anthropic',
            family: 'Claude 3.5'
        },
        capabilities: ['vision'],
        icon: '/logos/anthropic.svg',
        isNew: false,
        planRequired: 'pro',
        provider: 'Anthropic',
        maxTokens: 2048
    },
    {
        id: 'claude-3-5-haiku-20241022',
        name: 'Claude 3.5 Haiku',
        description: 'Fast and cost-effective model from Anthropic',
        category: {
            provider: 'Anthropic',
            family: 'Claude 3.5',
            showSeparator: true
        },
        capabilities: [],
        icon: '/logos/anthropic.svg',
        planRequired: 'plus',
        provider: 'Anthropic',
        maxTokens: 2048
    },

    // Meta Models
    {
        id: 'meta-llama/llama-4-scout-17b-16e-instruct',
        name: 'Llama 4 Scout',
        description: 'Fast and efficient Meta model',
        category: {
            provider: 'Meta',
            family: 'Llama4'
        },
        capabilities: ['vision', 'optimized-speed'],
        icon: '/logos/meta.svg',
        planRequired: 'free',
        provider: 'Groq',
        maxTokens: 2048
    },
    {
        id: 'meta-llama/llama-4-maverick-17b-128e-instruct',
        name: 'Llama 4 Maverick',
        description: 'Flagship model from Meta',
        category: {
            provider: 'Meta',
            family: 'Llama4',
            showSeparator: true
        },
        capabilities: ['vision', 'optimized-speed'],
        icon: '/logos/meta.svg',
        planRequired: 'plus',
        provider: 'Groq',
        maxTokens: 8192
    },
    {
        id: 'grok-3-mini-beta',
        name: 'Grok 3 Mini',
        description: 'Latest model from Grok',
        category: {
            provider: 'Xai',
            isStandalone: true,
            showSeparator: true
        },
        capabilities: ['reasoning'],
        icon: '/logos/xai.svg',
        planRequired: 'plus',
        provider: 'Xai',
        maxTokens: 4096
    },
    {
        id: 'gemini-2.5-pro-preview-03-25',
        name: 'Gemini 2.5 Pro',
        description: 'Google\'s advanced multimodal AI model',
        category: {
            provider: 'Google',
            family: 'Gemini 2.5',
            isStandalone: false,
            showSeparator: true
        },
        capabilities: ['reasoning', 'vision'],
        icon: '/logos/gemini.svg',
        planRequired: 'pro',
        maintenance: false,
        provider: 'Gemini',
        maxTokens: 2048
    },
    {
        id: 'gemini-2.5-flash-preview-04-17',
        name: 'Gemini 2.5 Flash',
        description: 'Great for daily tasks.',
        category: {
            provider: 'Google',
            family: 'Gemini 2.5',
            isStandalone: false,
            showSeparator: true
        },
        capabilities: ['reasoning', 'vision'],
        icon: '/logos/gemini.svg',
        planRequired: 'pro',
        maintenance: false,
        provider: 'Gemini',
        maxTokens: 2048
    },
    {
        id: 'qwen-qwq-32b',
        name: 'QWQ-32B',
        description: 'Advanced model from Qwen for various tasks',
        category: {
            provider: 'Qwen',
            isStandalone: true,
            showSeparator: true
        },
        capabilities: ['vision', 'reasoning'],
        icon: '/logos/qwen.svg',
        planRequired: 'plus',
        provider: 'Groq',
        maxTokens: 8192
    },
    {
        id: 'deepseek-r1-distill-llama-70b',
        name: 'DeepSeek R1',
        description: 'Advanced model from DeepSeek for various tasks',
        category: {
            provider: 'DeepSeek',
            isStandalone: true,
            showSeparator: true
        },
        capabilities: ['vision', 'reasoning'],
        icon: '/logos/deepseek.svg',
        planRequired: 'plus',
        provider: 'Groq',
        maxTokens: 4096
    },
];

/**
 * Get a model by ID
 */
export const getModelById = (id: string): Model | undefined => {
    const modelMap = new Map(models.map(model => [model.id, model]));
    return modelMap.get(id);
};

/**
 * Get models by hierarchical category
 * @param categoryFilter The category to filter by
 * @param isAdmin Whether the user is an admin
 * @returns Array of models matching the category
 */
export const getModelsByHierarchicalCategory = (categoryFilter: string | ModelCategory, isAdmin: boolean = false): Model[] => {
    const filterCategory = parseCategory(categoryFilter);

    return models.filter(model => {
        const modelCategory = parseCategory(model.category);
        const providerMatch = modelCategory.provider === filterCategory.provider;
        const familyMatch = !filterCategory.family || modelCategory.family === filterCategory.family;
        const maintenanceCheck = model.planRequired !== 'maintenance' || isAdmin;

        return providerMatch && familyMatch && maintenanceCheck;
    });
};

/**
 * Get models by category
 */
export const getModelsByCategory = (category: string, isAdmin: boolean = false): Model[] => {
    return models.filter(model => {
        const modelCategory = parseCategory(model.category);
        return modelCategory.provider === category &&
            (model.planRequired !== 'maintenance' || isAdmin);
    });
};

/**
 * Get models by capability
 */
export const getModelsByCapability = (capability: ModelCapability, isAdmin: boolean = false): Model[] => {
    return models.filter(model =>
        model.capabilities?.includes(capability) &&
        (model.planRequired !== 'maintenance' || isAdmin)
    );
};

/**
 * Check if a model has a specific capability
 */
export const modelHasCapability = (modelId: string, capability: ModelCapability): boolean => {
    const model = getModelById(modelId);
    return model?.capabilities?.includes(capability) || false;
}; 